import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { THEMES, DEFAULT_THEME } from '../constants/themes';
import { useAuth } from './AuthContext';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [currentTheme, setCurrentTheme] = useState(DEFAULT_THEME);
  const [loading, setLoading] = useState(true);

  // Load user's theme preference on mount and when user changes
  useEffect(() => {
    loadThemePreference();
  }, [user, isAuthenticated]);

  // Apply theme to document
  useEffect(() => {
    applyTheme(currentTheme);
  }, [currentTheme]);

  const loadThemePreference = async () => {
    try {
      if (isAuthenticated && user?.id) {
        // Load from database
        const { data, error } = await supabase
          .from('profiles')
          .select('theme_preference')
          .eq('id', user.id)
          .single();

        if (!error && data?.theme_preference && THEMES[data.theme_preference]) {
          setCurrentTheme(data.theme_preference);
        }
      } else {
        // Load from localStorage for non-authenticated users
        const savedTheme = localStorage.getItem('theme_preference');
        if (savedTheme && THEMES[savedTheme]) {
          setCurrentTheme(savedTheme);
        }
      }
    } catch (error) {
      console.error('Error loading theme preference:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = (themeId) => {
    const theme = THEMES[themeId] || THEMES[DEFAULT_THEME];
    const root = document.documentElement;

    // Apply CSS custom properties
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });

    root.style.setProperty('--gradient-primary', theme.gradient);
    root.style.setProperty('--shadow-primary', theme.shadow);

    // Apply derived/semantic colors
    const hexToRgba = (hex, alpha) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    root.style.setProperty('--color-hover', hexToRgba(theme.colors.primary, 0.1));
    root.style.setProperty('--color-active', hexToRgba(theme.colors.primary, 0.2));
    root.style.setProperty('--color-overlay', 'rgba(0, 0, 0, 0.6)');

    // Add meta theme-color for mobile browsers
    let metaTheme = document.querySelector('meta[name="theme-color"]');
    if (!metaTheme) {
      metaTheme = document.createElement('meta');
      metaTheme.name = 'theme-color';
      document.head.appendChild(metaTheme);
    }
    metaTheme.content = theme.colors.primary;

    // Add theme class to body for additional styling
    document.body.className = `theme-${themeId}`;
  };

  const changeTheme = async (themeId) => {
    if (!THEMES[themeId]) {
      console.error('Invalid theme ID:', themeId);
      return { success: false, error: 'Invalid theme' };
    }

    try {
      setCurrentTheme(themeId);

      if (isAuthenticated && user?.id) {
        // Save to database
        const { error } = await supabase
          .from('profiles')
          .update({ theme_preference: themeId })
          .eq('id', user.id);

        if (error) throw error;
      } else {
        // Save to localStorage
        localStorage.setItem('theme_preference', themeId);
      }

      return { success: true };
    } catch (error) {
      console.error('Error saving theme preference:', error);
      return { success: false, error: error.message };
    }
  };

  const resetTheme = async () => {
    return changeTheme(DEFAULT_THEME);
  };

  const value = {
    currentTheme,
    currentThemeConfig: THEMES[currentTheme],
    availableThemes: THEMES,
    changeTheme,
    resetTheme,
    loading,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
